import { useEffect, useRef, useState } from "react";
import { Lock, ShieldCheck, Loader2 } from "lucide-react";
import { stripeAppearance } from "@/lib/theme";

interface Props {
  /** Confirm an EXISTING intent (e.g. retrying an open invoice). */
  clientSecret?: string;
  /** DEFERRED mode — no Stripe intent exists until the user clicks Pay:
      the element mounts from `amount` (dollars) alone, and `createIntent`
      runs on the pay click to mint the intent. Abandoned forms therefore
      leave no Incomplete entries in Stripe. */
  amount?: number;
  createIntent?: () => Promise<string>;
  buttonLabel: string;
  onSuccess: (paymentIntentId: string) => Promise<void>;
  disabled?: boolean;
  /** Store the card on the Stripe customer for one-click re-use. Must mirror the
      intent's `setup_future_usage` — Stripe rejects the confirm if they disagree. */
  saveCard?: boolean;
  /** "setup" saves a card without charging it (add / replace card on file). The
      element then confirms a SetupIntent, and `onSuccess` receives the SETUP
      intent id — not a payment intent id. Needs no `amount`. */
  intentType?: "payment" | "setup";
}

export function StripePaymentElement({ clientSecret, amount, createIntent, buttonLabel, onSuccess, disabled, saveCard, intentType = "payment" }: Props) {
  const isSetup = intentType === "setup";
  const stripeElementsRef = useRef<any>(null);
  const paymentElementRef = useRef<any>(null);
  // Deferred-mode intent created on the first pay click — reused on a retry
  // after a decline so we don't mint a second intent.
  const deferredSecretRef = useRef<string | null>(null);
  const [elementReady, setElementReady] = useState(false);
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState("");

  useEffect(() => {
    // Setup mode carries no amount — it mounts deferred from `mode: "setup"` alone.
    if (!clientSecret && amount == null && !isSetup) return;
    let cancelled = false;

    const timer = setTimeout(() => {
      if (cancelled) return;
      const stripe = (window as any).Stripe;
      if (!stripe) return;

      const mountPoint = document.getElementById("stripe-payment-element-mount");
      if (!mountPoint) return;
      mountPoint.innerHTML = "";

      const elements = stripe.elements({
        ...(clientSecret
          ? { clientSecret }
          : isSetup
          ? {
              mode: "setup",
              currency: "usd",
              setupFutureUsage: "off_session",
              // Must mirror the SetupIntent's `payment_method_types=["card"]`. Left
              // unset, deferred Elements mounts with automatic payment methods and
              // Stripe rejects the confirm outright: "Payment details were collected
              // through Stripe Elements using automatic payment methods and cannot be
              // confirmed through the API configured with payment_method_types."
              // Card-only is also what the rest of the app can use — the saved card is
              // read back with PaymentMethod.list(type="card") and charged off-session
              // for renewals and one-click unlocks, so a Klarna/Cash App mandate saved
              // here would be invisible to `get_saved_card` and unusable for billing.
              paymentMethodTypes: ["card"],
            }
          : {
              mode: "payment",
              amount: Math.round((amount as number) * 100),
              currency: "usd",
              ...(saveCard ? { setupFutureUsage: "off_session" } : {}),
            }),
        // Gold-accented appearance following the active light/dark theme.
        appearance: stripeAppearance(),
      });

      const paymentEl = elements.create("payment", {
        // Accordion lists every payment method as a full-width row — all options stay
        // visible and it stays responsive on narrow screens (no horizontal overflow).
        layout: { type: "accordion", defaultCollapsed: false, radios: true, spacedAccordionItems: true },
      });
      paymentEl.mount(mountPoint);
      stripeElementsRef.current = elements;
      paymentElementRef.current = paymentEl;
      setElementReady(true);
    }, 80);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      if (paymentElementRef.current) {
        try { paymentElementRef.current.unmount(); } catch { /* ignore */ }
        paymentElementRef.current = null;
      }
      stripeElementsRef.current = null;
      deferredSecretRef.current = null;
      setElementReady(false);
    };
  }, [clientSecret, amount, saveCard, isSetup]);

  const handlePay = async () => {
    const stripe = (window as any).Stripe;
    const elements = stripeElementsRef.current;
    if (!stripe || !elements) return;
    setPaying(true);
    setPayError("");
    try {
      let secret = clientSecret ?? null;
      if (!secret) {
        // Deferred flow: validate the form first (the element renders its own
        // messages), and only then create the intent — abandoned forms never
        // reach Stripe. Reuse the intent on a retry after a decline.
        const { error: submitError } = await elements.submit();
        if (submitError) return;
        secret = deferredSecretRef.current ?? (await createIntent!());
        deferredSecretRef.current = secret;
      }
      const confirmArgs = {
        elements,
        ...(clientSecret ? {} : { clientSecret: secret }),
        confirmParams: { return_url: window.location.href },
        redirect: "if_required" as const,
      };

      if (isSetup) {
        // Saving a card: nothing is charged, and the id handed back is a SetupIntent's.
        const { error, setupIntent } = await stripe.confirmSetup(confirmArgs);
        if (error) {
          setPayError(error.message ?? "Could not save the card. Please try again.");
        } else if (setupIntent && setupIntent.status === "succeeded") {
          await onSuccess(setupIntent.id);
        } else {
          setPayError("The card was not saved. Please try again.");
        }
        return;
      }

      const { error, paymentIntent } = await stripe.confirmPayment(confirmArgs);

      if (error) {
        setPayError(error.message ?? "Payment failed. Please try again.");
      } else if (paymentIntent && (paymentIntent.status === "succeeded" || paymentIntent.status === "processing")) {
        await onSuccess(paymentIntent.id);
      } else {
        setPayError("Payment was not completed. Please try again.");
      }
    } catch (err) {
      setPayError(err instanceof Error ? err.message : "Payment failed. Please try again.");
    } finally {
      setPaying(false);
    }
  };

  return (
    <div className="space-y-4">
      <div id="stripe-payment-element-mount" className="min-h-[80px]" />
      {!elementReady && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading secure payment form…
        </div>
      )}
      {payError && <p className="text-xs text-destructive">{payError}</p>}
      <button
        onClick={handlePay}
        disabled={paying || !elementReady || disabled}
        className="w-full h-12 rounded-xl bg-gold-gradient text-primary-foreground text-sm font-bold flex items-center justify-center gap-2 shadow-md hover:brightness-[1.06] disabled:opacity-60 transition-[filter]"
      >
        {paying
          ? <><Loader2 className="h-4 w-4 animate-spin" />Processing…</>
          : <><Lock className="h-4 w-4" />{buttonLabel}</>}
      </button>
      <div className="flex items-center justify-center gap-4 text-[11px] text-muted-foreground">
        <span className="flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5 text-emerald-500" />256-bit encryption</span>
        <span className="text-border">|</span>
        <span className="flex items-center gap-1"><Lock className="h-3 w-3" />Powered by Stripe</span>
      </div>
    </div>
  );
}
